import React, { useContext, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  MenuItem,
  Paper,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import SaveIcon from '@mui/icons-material/Save';
import CodeMirror from '@uiw/react-codemirror';
import { sql } from '@codemirror/lang-sql';
import { createTheme } from '@uiw/codemirror-themes';
import { tags as t } from '@lezer/highlight';
import { DataContext } from '../../../../context/DataContext';
import WorkspaceSelector from '../../../../components/WorkspaceSelector';
import {
  ADMIN_COLORS,
  darkFieldSx,
  darkPaperSx,
  darkTableSx,
  outlinedButtonSx
} from '../../components/adminStyles';
import SqlWorkbookService from '../../../../services/SqlWorkbookService';
import IcebergCatalogService from '../../../../services/IcebergCatalogService';

// Extensión de CodeMirror fuera del componente -- no depende de props/state,
// recrearla en cada render forzaría a @uiw/react-codemirror a reconciliar
// las extensiones de más.
const SQL_EDITOR_EXTENSIONS = [sql()];

// Mismo azul de acento que el menú lateral (ver AdminHomeLayout.jsx --
// íconos e ítem seleccionado, #38bdf8).
const SIDEBAR_ACCENT_BLUE = 'var(--wb-accent)';

// Theme oscuro del editor -- combina con el fondo del panel admin
// (AdminHomeLayout.jsx: main #0b1120, tarjetas #121827 como Section en
// AdminContentWrapper.jsx) en vez del theme claro por defecto de
// CodeMirror. Todos los tokens usan colores CLAROS/vivos a propósito
// (nunca gris oscuro ni negro) para que ninguna palabra clave, nombre de
// tabla/columna o string quede ilegible contra el fondo oscuro.
const SQL_EDITOR_THEME = createTheme({
  theme: 'dark', // solo afecta valores por defecto; los colores vienen del tema activo
  settings: {
    background: 'var(--wb-bg-panel)',
    foreground: 'var(--wb-text)',
    caret: SIDEBAR_ACCENT_BLUE,
    selection: 'rgba(56, 189, 248, 0.25)',
    selectionMatch: 'rgba(56, 189, 248, 0.35)',
    lineHighlight: 'rgba(148, 163, 184, 0.08)',
    gutterBackground: 'var(--wb-bg-surface)',
    gutterForeground: 'var(--wb-x-code-64748b, #64748b)',
    gutterBorder: 'transparent',
    fontFamily: 'monospace'
  },
  styles: [
    { tag: t.keyword, color: SIDEBAR_ACCENT_BLUE, fontWeight: 'bold' },
    { tag: [t.name, t.deleted, t.character, t.propertyName, t.macroName], color: 'var(--wb-text)' },
    { tag: [t.function(t.variableName), t.labelName], color: 'var(--wb-x-code-7dd3fc, #7dd3fc)' },
    { tag: [t.definition(t.name), t.separator], color: 'var(--wb-text)' },
    { tag: [t.typeName, t.className, t.changed, t.annotation, t.modifier, t.self, t.namespace], color: 'var(--wb-orange)' },
    { tag: [t.number, t.atom, t.bool, t.special(t.variableName)], color: 'var(--wb-warning)' },
    { tag: [t.operator, t.operatorKeyword], color: 'var(--wb-muted)' },
    { tag: [t.url, t.escape, t.regexp, t.link], color: 'var(--wb-x-code-86efac, #86efac)' },
    { tag: t.string, color: 'var(--wb-x-code-a3e635, #a3e635)' },
    { tag: [t.meta, t.comment], color: 'var(--wb-muted)', fontStyle: 'italic' },
    { tag: t.invalid, color: 'var(--wb-danger)' }
  ]
});

// Valor de la pestaña "+" (nueva hoja) -- nunca coincide con un sheet_id
// real, así que sirve como centinela para distinguirla en el onChange de
// <Tabs>.
const NEW_SHEET_TAB_VALUE = '__new_sheet__';

const asList = (response) => {
  if (!response) return [];
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.items)) return response.items;
  return [];
};

/**
 * SQL Workbook -- editor de SQL tipo Snowflake Worksheets sobre las
 * tablas Iceberg del tenant (vía Athena). Cada hoja guardada es PRIVADA
 * de quien la crea (decisión explícita, ver sql_workbook_sheet/app.py) y
 * cuelga del workspace actualmente seleccionado (mismo `selectedWorkspace`
 * de DataContext que ya usa el resto de la app -- no un workspace_id
 * elegido en la URL). La ejecución corre contra
 * whisperbi-agent-management-strands (SqlWorkbookService.executeQuery),
 * validada de solo-lectura y acotada a las bases Iceberg del tenant del
 * lado del servidor -- este componente nunca decide qué está permitido,
 * solo muestra lo que el backend ya resolvió.
 */
const SqlWorkbook = () => {
  const { accountId, selectedWorkspace } = useContext(DataContext);
  const workspaceId = selectedWorkspace?.semantic_workspace_id;

  const [sheets, setSheets] = useState([]);
  const [isLoadingSheets, setIsLoadingSheets] = useState(false);
  const [activeSheetId, setActiveSheetId] = useState(null);

  const [name, setName] = useState('');
  const [sqlText, setSqlText] = useState('SELECT * FROM ');
  const [databases, setDatabases] = useState([]);
  const [schemas, setSchemas] = useState([]);
  const [icebergDatabaseId, setIcebergDatabaseId] = useState('');
  const [icebergSchemaId, setIcebergSchemaId] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const [isRunning, setIsRunning] = useState(false);
  const [queryError, setQueryError] = useState(null);
  const [queryResult, setQueryResult] = useState(null);

  const selectedSchema = schemas.find((s) => s.iceberg_schema_id === icebergSchemaId);

  useEffect(() => {
    if (!accountId) return;
    IcebergCatalogService.listDatabases(accountId)
      .then((resp) => setDatabases(asList(resp)))
      .catch((err) => console.error('Error cargando databases Iceberg:', err));
  }, [accountId]);

  useEffect(() => {
    if (!accountId || !icebergDatabaseId) {
      setSchemas([]);
      return;
    }
    IcebergCatalogService.listSchemas(accountId, { icebergDatabaseId })
      .then((resp) => setSchemas(asList(resp)))
      .catch((err) => console.error('Error cargando schemas Iceberg:', err));
  }, [accountId, icebergDatabaseId]);

  const loadSheets = async () => {
    if (!workspaceId) return;
    setIsLoadingSheets(true);
    try {
      const resp = await SqlWorkbookService.listSheets(workspaceId);
      setSheets(asList(resp));
    } catch (err) {
      console.error('Error cargando hojas del SQL Workbook:', err);
    } finally {
      setIsLoadingSheets(false);
    }
  };

  useEffect(() => {
    loadSheets();
    setActiveSheetId(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceId]);

  const resetEditor = () => {
    setActiveSheetId(null);
    setName('');
    setSqlText('SELECT * FROM ');
    setIcebergDatabaseId('');
    setIcebergSchemaId('');
    setQueryResult(null);
    setQueryError(null);
  };

  const openSheet = (sheet) => {
    setActiveSheetId(sheet.sheet_id);
    setName(sheet.name || '');
    setSqlText(sheet.sql_text || '');
    setIcebergDatabaseId(sheet.iceberg_database_id || '');
    setIcebergSchemaId(sheet.iceberg_schema_id || '');
    setQueryResult(null);
    setQueryError(null);
  };

  const handleSave = async () => {
    if (!workspaceId || !name.trim()) return;
    setIsSaving(true);
    try {
      if (activeSheetId) {
        await SqlWorkbookService.updateSheet(workspaceId, activeSheetId, {
          name: name.trim(), sqlText, icebergDatabaseId, icebergSchemaId
        });
      } else {
        const created = await SqlWorkbookService.createSheet(workspaceId, {
          name: name.trim(), sqlText, icebergDatabaseId, icebergSchemaId
        });
        setActiveSheetId(created.sheet_id);
      }
      await loadSheets();
    } catch (err) {
      console.error('Error guardando la hoja del SQL Workbook:', err);
      setQueryError(err.message || 'No se pudo guardar la hoja.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (sheet) => {
    if (!workspaceId) return;
    try {
      await SqlWorkbookService.deleteSheet(workspaceId, sheet.sheet_id);
      if (activeSheetId === sheet.sheet_id) {
        resetEditor();
      }
      await loadSheets();
    } catch (err) {
      console.error('Error borrando la hoja del SQL Workbook:', err);
    }
  };

  const handleRun = async () => {
    if (!selectedSchema?.glue_database_name || !sqlText.trim()) return;
    setIsRunning(true);
    setQueryError(null);
    setQueryResult(null);
    try {
      const result = await SqlWorkbookService.executeQuery(selectedSchema.glue_database_name, sqlText);
      setQueryResult(result);
    } catch (err) {
      console.error('Error ejecutando la consulta del SQL Workbook:', err);
      setQueryError(err.message || 'No se pudo ejecutar la consulta.');
    } finally {
      setIsRunning(false);
    }
  };

  const resultColumns = queryResult?.rows?.length ? Object.keys(queryResult.rows[0]) : [];

  return (
    <Box>
      <Box sx={{ mb: 2, maxWidth: 360 }}>
        <WorkspaceSelector />
      </Box>

      {!workspaceId ? (
        <Alert severity="info">Seleccioná un workspace arriba para usar el SQL Workbook.</Alert>
      ) : (
      <Box sx={{ minHeight: 480 }}>
        <Box sx={{ borderBottom: 1, borderColor: ADMIN_COLORS.border, mb: 2 }}>
          {isLoadingSheets ? (
            <Box sx={{ display: 'flex', alignItems: 'center', py: 1 }}>
              <CircularProgress size={20} />
            </Box>
          ) : (
            <Tabs
              value={activeSheetId || NEW_SHEET_TAB_VALUE}
              onChange={(event, newValue) => {
                if (newValue === NEW_SHEET_TAB_VALUE) {
                  resetEditor();
                  return;
                }
                const sheet = sheets.find((s) => s.sheet_id === newValue);
                if (sheet) openSheet(sheet);
              }}
              variant="scrollable"
              scrollButtons="auto"
              TabIndicatorProps={{ style: { backgroundColor: SIDEBAR_ACCENT_BLUE } }}
            >
              {sheets.map((sheet) => (
                <Tab
                  key={sheet.sheet_id}
                  value={sheet.sheet_id}
                  sx={{
                    textTransform: 'none',
                    color: SIDEBAR_ACCENT_BLUE,
                    '&.Mui-selected': { color: SIDEBAR_ACCENT_BLUE }
                  }}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <span>{sheet.name}</span>
                      <Tooltip title="Borrar hoja">
                        <IconButton
                          component="span"
                          size="small"
                          onClick={(event) => {
                            event.stopPropagation();
                            handleDelete(sheet);
                          }}
                          sx={{ p: 0.25, color: 'inherit' }}
                        >
                          <DeleteOutlineIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  }
                />
              ))}
              <Tab
                value={NEW_SHEET_TAB_VALUE}
                icon={<AddIcon sx={{ fontSize: 20 }} />}
                aria-label="Nueva hoja"
                sx={{
                  minWidth: 48,
                  color: SIDEBAR_ACCENT_BLUE,
                  '&.Mui-selected': { color: SIDEBAR_ACCENT_BLUE }
                }}
              />
            </Tabs>
          )}
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2 }}>
            <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
              <Button
                variant="outlined"
                startIcon={<PlayArrowIcon />}
                onClick={handleRun}
                disabled={isRunning || !icebergSchemaId || !sqlText.trim()}
                sx={outlinedButtonSx}
              >
                {isRunning ? 'Ejecutando…' : 'Ejecutar'}
              </Button>
              <Button
                variant="outlined"
                startIcon={<SaveIcon />}
                onClick={handleSave}
                disabled={isSaving || !name.trim()}
                sx={outlinedButtonSx}
              >
                {isSaving ? 'Guardando…' : 'Guardar hoja'}
              </Button>
            </Box>

            <TextField
              label="Nombre de la hoja"
              value={name}
              onChange={(event) => setName(event.target.value)}
              size="small"
              sx={{ ...darkFieldSx, flex: 1, minWidth: 180 }}
            />
            <TextField
              select
              label="Database"
              value={icebergDatabaseId}
              onChange={(event) => {
                setIcebergDatabaseId(event.target.value);
                setIcebergSchemaId('');
              }}
              size="small"
              sx={{ ...darkFieldSx, minWidth: 200 }}
            >
              {databases.map((db) => (
                <MenuItem key={db.iceberg_database_id} value={db.iceberg_database_id}>
                  {db.name}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Schema"
              value={icebergSchemaId}
              onChange={(event) => setIcebergSchemaId(event.target.value)}
              disabled={!icebergDatabaseId}
              size="small"
              sx={{ ...darkFieldSx, minWidth: 200 }}
            >
              {schemas.map((schema) => (
                <MenuItem key={schema.iceberg_schema_id} value={schema.iceberg_schema_id}>
                  {schema.name}
                </MenuItem>
              ))}
            </TextField>
          </Box>

          <Box>
            <Typography variant="caption" sx={{ display: 'block', mb: 0.5, color: ADMIN_COLORS.muted }}>
              SQL
            </Typography>
            <Box
              sx={{
                border: '1px solid',
                borderColor: 'var(--wb-border-16)',
                borderRadius: 1,
                overflow: 'hidden'
              }}
            >
              <CodeMirror
                value={sqlText}
                onChange={(value) => setSqlText(value)}
                extensions={SQL_EDITOR_EXTENSIONS}
                theme={SQL_EDITOR_THEME}
                height="max(360px, calc(100vh - 380px))"
                basicSetup={{ foldGutter: false }}
                style={{ fontSize: '0.9rem' }}
              />
            </Box>
          </Box>

          {queryError && <Alert severity="error">{queryError}</Alert>}

          {queryResult && (
            <Paper variant="outlined" sx={{ ...darkPaperSx, flex: 1, overflow: 'auto' }}>
              <Box sx={{ px: 2, py: 1 }}>
                <Typography variant="caption" sx={{ color: ADMIN_COLORS.muted }}>
                  {queryResult.row_count} fila(s){queryResult.truncated ? ' (resultado truncado)' : ''}
                  {typeof queryResult.data_scanned_bytes === 'number'
                    ? ` · ${(queryResult.data_scanned_bytes / 1_000_000).toFixed(2)} MB escaneados`
                    : ''}
                </Typography>
              </Box>
              <Divider sx={{ borderColor: ADMIN_COLORS.border }} />
              <TableContainer>
                <Table size="small" stickyHeader sx={darkTableSx}>
                  <TableHead>
                    <TableRow>
                      {resultColumns.map((col) => (
                        <TableCell key={col}>{col}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(queryResult.rows || []).map((row, idx) => (
                      // eslint-disable-next-line react/no-array-index-key
                      <TableRow key={idx}>
                        {resultColumns.map((col) => (
                          <TableCell key={col}>{row[col]}</TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}
        </Box>
      </Box>
      )}
    </Box>
  );
};

export default SqlWorkbook;
